import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDraftMode } from "@/contexts/DraftModeContext";
import { CONTROL_HEIGHT, SURFACE } from "@/theme/tokens";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";

type RefreshIconButtonProps = { onClick?: () => void; areThereAnyUpdates: boolean; isRefreshingNow: boolean };

export const RefreshIconButton: FC<RefreshIconButtonProps> = ({ onClick, areThereAnyUpdates, isRefreshingNow }) => {
	const { isDraftMode } = useDraftMode();
	const { t } = useTranslation();

	const tooltipTitle = useMemo<string>(() => {
		if (isDraftMode) return t("refresh.disabled.draft.mode");
		if (isRefreshingNow) return t("refresh.loading");
		if (areThereAnyUpdates) return t("refresh.updates.available");
		return t("refresh.up.to.date");
	}, [isDraftMode, isRefreshingNow, areThereAnyUpdates, t]);

	return (
		<InHouseTooltip title={tooltipTitle}>
			<Box
				sx={{
					position: "relative",
					width: "fit-content",
				}}
			>
				{isRefreshingNow ? (
					// The spinner stands in for the button, so it has to occupy the button's slot: this branch is a
					// plain Box, which the header group's `.MuiIconButton-root` rule doesn't reach, and at 36px wide
					// with the spinner's intrinsic height it shrank the slot and nudged everything to its right by
					// 4px for the duration of every refresh.
					<Box
						sx={{
							width: CONTROL_HEIGHT.STANDARD,
							height: CONTROL_HEIGHT.STANDARD,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CircularProgress size={"16px"} sx={{ "&.MuiCircularProgress-root": { color: SURFACE } }} />
					</Box>
				) : (
					<IconButton data-testid="header-refresh-button" onClick={onClick} disabled={isDraftMode}>
						<RefreshOutlined fontSize={"small"} style={{ color: isDraftMode ? alpha(SURFACE, 0.3) : SURFACE }} />
					</IconButton>
				)}
				{areThereAnyUpdates && !isRefreshingNow && (
					<Box
						style={{ borderRadius: "50%", background: "orange", width: 8, height: 8 }}
						sx={{
							position: "absolute",
							top: "8%",
							right: "8%",
						}}
					/>
				)}
			</Box>
		</InHouseTooltip>
	);
};
